import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import { alpha, useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import { labelStatusLogistico } from "utils/pedidoOperacional";
import { formatDate } from "helpers/formatDate";

type PedidoExpedicaoResumoProps = {
  statusLogistico: string;
  provedor: string | null | undefined;
  servico: string | null | undefined;
  rastreio: string | null | undefined;
  enviadoEm: string | null | undefined;
  entregueEm: string | null | undefined;
  entregaConcluida: boolean;
};

function fmt(iso: string | null | undefined) {
  if (!iso) return "—";
  try {
    return formatDate(iso, "dd/MM/yyyy HH:mm:ss");
  } catch {
    return String(iso);
  }
}

function dash(v: string | null | undefined) {
  const t = v?.trim();
  return t ? t : "—";
}

/**
 * Campos principais da expedição: status, transportadora, rastreio e datas.
 */
export default function PedidoExpedicaoResumo({
  statusLogistico,
  provedor,
  servico,
  rastreio,
  enviadoEm,
  entregueEm,
  entregaConcluida,
}: PedidoExpedicaoResumoProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: entregaConcluida
          ? `2px solid ${theme.palette.success.main}`
          : `1px solid ${borderSoft}`,
        boxShadow: entregaConcluida
          ? `0 0 0 1px ${alpha(theme.palette.success.main, 0.2)}`
          : "0 2px 12px rgba(0,0,0,0.04)",
      }}
    >
      <MDBox p={2.5}>
        <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
          <Typography variant="button" fontWeight={700} color="primary">
            Situação do envio
          </Typography>
          <Chip
            size="small"
            label={labelStatusLogistico(statusLogistico)}
            color={
              entregaConcluida
                ? "success"
                : statusLogistico === "POSTADO" ||
                    statusLogistico === "EM_TRANSITO"
                  ? "info"
                  : statusLogistico === "ETIQUETA_GERADA"
                    ? "warning"
                    : "default"
            }
          />
        </MDBox>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Provedor
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.5}>
              {dash(provedor)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Serviço
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.5}>
              {dash(servico)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="caption" color="text.secondary">
              Rastreio
            </Typography>
            <Typography variant="body2" fontWeight={600} mt={0.5} sx={{ wordBreak: "break-all" }}>
              {dash(rastreio)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Data de envio (postagem)
            </Typography>
            <Typography variant="body2" mt={0.5}>
              {fmt(enviadoEm)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Data de entrega
            </Typography>
            <Typography variant="body2" mt={0.5}>
              {fmt(entregueEm)}
            </Typography>
          </Grid>
        </Grid>
      </MDBox>
    </Card>
  );
}
